
import React, { useState, useEffect } from 'react';
import { Menu, X, Palmtree, Upload, Loader2, ChevronRight } from 'lucide-react';
import { NavItem } from '../types';
import { uploadService } from '../services/uploadService';
import { useWindowSize } from '../hooks/useWindowSize';

interface NavbarProps {
  isEditable: boolean;
  currentView: string;
  onNavigate: (view: any) => void;
  siteName: string;
  logoUrl?: string;
  onUpdateSiteName: (name: string) => void;
  onUpdateLogo?: (url: string) => void;
  navLinks: NavItem[];
  onUpdateNavLinks: (links: NavItem[]) => void;
}

const Navbar: React.FC<NavbarProps> = ({ isEditable, currentView, onNavigate, siteName, logoUrl, onUpdateSiteName, onUpdateLogo, navLinks, onUpdateNavLinks }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { width } = useWindowSize();

  // Close mobile menu if window is resized to desktop width
  useEffect(() => {
    if (width >= 768 && isOpen) {
      setIsOpen(false);
    }
  }, [width, isOpen]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleUpdateNavLink = (id: string, newLabel: string) => {
    onUpdateNavLinks(navLinks.map(link => link.id === id ? { ...link, label: newLabel } : link));
  };

  const handleLogoUpload = async () => {
    try {
      setIsUploading(true);
      const base64 = await uploadService.selectAndUpload();
      if (onUpdateLogo) onUpdateLogo(base64);
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  // Shared branding component to ensure identical rendering
  const Branding = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`flex items-center gap-4 group cursor-pointer ${mobile ? '' : ''}`} onClick={() => onNavigate('home')}>
      <div className="relative group/logo flex items-center gap-3">
        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-xl transition-transform group-hover:scale-110">
          <Palmtree className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
        </div>
        <div className="flex flex-col">
          {isEditable ? (
            <input 
              value={siteName}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onUpdateSiteName(e.target.value)}
              className="bg-transparent font-extrabold tracking-widest text-[12px] md:text-[10px] uppercase focus:outline-none border-b border-dashed border-sky-400 text-white font-sans"
            />
          ) : (
            <span className="text-[12px] md:text-[10px] font-extrabold tracking-widest uppercase text-white font-sans">{siteName}</span>
          )}
          <span className="text-[7px] md:text-[6px] font-bold tracking-luxury uppercase opacity-40 text-white">SARANGANI DAVAO OCCIDENTAL</span>
        </div>
        {isEditable && (
          <button onClick={(e) => { e.stopPropagation(); handleLogoUpload(); }} className="absolute -top-1.5 -right-1.5 bg-sky-500 text-white p-1 rounded-full opacity-0 group-hover/logo:opacity-100 transition-all">
            {isUploading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Upload className="w-2.5 h-2.5" />}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <nav className={`fixed w-full z-50 transition-all duration-700 ${isScrolled || currentView !== 'home' ? 'bg-slate-950/95 backdrop-blur-2xl py-2' : 'bg-transparent py-5'}`}>
      <div className="px-6 md:px-10 lg:px-12 flex justify-between items-center w-full">
        <Branding />

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((item) => (
            <div key={item.id}>
              {isEditable ? (
                <input 
                  value={item.label}
                  onChange={(e) => handleUpdateNavLink(item.id, e.target.value)}
                  className="bg-transparent text-[9px] font-bold uppercase tracking-[0.3em] focus:outline-none border-b border-dashed border-sky-400 w-20 text-center text-white/80"
                />
              ) : (
                <button onClick={() => onNavigate(item.id)} className={`text-[9px] font-bold uppercase tracking-[0.3em] transition-all relative py-1 hover:text-sky-400 ${currentView === item.id ? 'text-sky-400 font-bold' : 'text-white/70'}`}>
                  {item.label}
                </button>
              )}
            </div>
          ))}
          <button onClick={() => onNavigate('contact')} className="px-8 py-3 rounded-full font-bold text-[9px] uppercase tracking-luxury bg-white text-slate-950 hover:bg-sky-500 hover:text-white transition-all shadow-lg active:scale-95">Plan Your Visit</button>
        </div>

        {/* Hamburger */}
        <button 
          className="md:hidden w-11 h-11 rounded-xl bg-white/10 text-white flex items-center justify-center transition-all active:scale-90" 
          onClick={() => setIsOpen(!isOpen)}
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 w-full h-screen bg-slate-950 z-[60] flex flex-col animate-in fade-in duration-500">
          {/* Header section identical to navbar structure */}
          <div className={`w-full transition-all duration-700 ${isScrolled || currentView !== 'home' ? 'py-2' : 'py-5'} px-6 border-b border-white/5`}>
            <div className="flex justify-between items-center">
              <Branding mobile />
              <button 
                onClick={() => setIsOpen(false)} 
                className="w-11 h-11 rounded-xl bg-white/10 border border-white/10 text-white flex items-center justify-center transition-all active:scale-90"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col px-8 pb-8 overflow-y-auto">
            <div className="flex flex-col mt-6">
              {navLinks.map((item) => (
                <button 
                  key={item.id} 
                  onClick={() => { onNavigate(item.id); setIsOpen(false); }} 
                  className={`group text-[11px] font-sans font-bold text-left py-5 border-b border-white/5 transition-all active:opacity-50 uppercase tracking-[0.3em] flex items-center justify-between ${currentView === item.id ? 'text-sky-400 pl-4' : 'text-white/60 hover:text-sky-400 hover:pl-4'}`}
                >
                  <span>{item.label}</span>
                  <ChevronRight className={`w-4 h-4 transition-all opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-2 ${currentView === item.id ? 'opacity-100 translate-x-0' : ''}`} />
                </button>
              ))}
            </div>

            <div className="mt-auto pt-8">
              <button 
                onClick={() => { onNavigate('contact'); setIsOpen(false); }} 
                className="w-full bg-white text-slate-950 py-5 rounded-2xl font-bold uppercase tracking-[0.2em] text-[10px] shadow-2xl shadow-white/5 active:scale-95 transition-all hover:bg-sky-500 hover:text-white"
              >
                Plan Your Visit
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
